import {
  advisorFunctionLabel,
  DISTRIBUTE_MAX_LEADS,
  MANAGER_ROLES,
  RANKING_WEIGHTS,
  ROLE_LABELS,
  type AssignmentRuleDto,
  type BotReadiness,
  type PoolAdvisorRow,
  suggestWeights,
  WEIGHT_TOTAL,
} from '@performance-crm/shared';
import { CalendarClock, Loader2, Shuffle, Trophy, UserPlus, Users } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { AssignDialog } from '@/components/leads/dialogs';
import { SchedulesTab } from '@/components/team/SchedulesTab';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import { distributeSummary, groupThousands } from '@/lib/bulkSummary';
import { fmt } from '@/lib/format';
import { currentMonth, monthLabel, monthOptions } from '@/lib/months';
import {
  useAdvisors,
  useAssignmentRules,
  useBotReadiness,
  useDistribute,
  usePool,
  useProjects,
  useRanking,
  useUpsertRule,
} from '@/lib/queries';
import { ruleAdvisorRows } from '@/lib/ruleAdvisors';
import { useCompanyScope } from '@/lib/useCompanyScope';

type Tab = 'bolsa' | 'ranking' | 'reglas' | 'horarios';

export function TeamPage() {
  const { user } = useAuth();
  const { companies, active } = useCompanyScope();
  const [params, setParams] = useSearchParams();
  const companyId =
    params.get('companyId') ??
    active ??
    companies.find((c) => MANAGER_ROLES.includes(c.role))?.companyId;
  const projectId = params.get('projectId') ?? undefined;
  const tab = (params.get('tab') as Tab) ?? 'bolsa';
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );
  const { data: projects } = useProjects();
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k === 'companyId') next.delete('projectId');
    setParams(next, { replace: true });
  };
  if (user && !user.memberships.some((m) => MANAGER_ROLES.includes(m.role)))
    return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Equipo</h1>
          <p className="text-muted-foreground text-sm">
            Bolsa de leads por asesor, reparto y ranking.
          </p>
        </div>
        <div className="bg-card flex gap-1 rounded-md border p-1">
          {(
            [
              ['bolsa', 'Bolsa', Users],
              ['ranking', 'Ranking', Trophy],
              ['reglas', 'Distribución', Shuffle],
              ['horarios', 'Horarios', CalendarClock],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => set('tab', key)}
              className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm ${tab === key ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
              data-testid={`tab-${key}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-card grid gap-2 rounded-lg border p-3 md:grid-cols-3">
        {companies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => set('companyId', e.target.value)}
            aria-label="Empresa"
          >
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}
        <Select
          value={projectId ?? ''}
          onChange={(e) => set('projectId', e.target.value)}
          aria-label="Proyecto"
        >
          <option value="">Todos los proyectos</option>
          {projects
            ?.filter((p) => p.companyId === companyId)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </Select>
      </div>
      {!isManager && (
        <p className="text-muted-foreground text-sm">No tienes rol de gestión en esta empresa.</p>
      )}
      {isManager && companyId && tab === 'bolsa' && (
        <PoolTab companyId={companyId} projectId={projectId} />
      )}
      {isManager && companyId && tab === 'ranking' && (
        <RankingTab companyId={companyId} projectId={projectId} />
      )}
      {isManager && companyId && tab === 'reglas' && <RulesTab companyId={companyId} />}
      {isManager && companyId && tab === 'horarios' && <SchedulesTab companyId={companyId} />}
    </div>
  );
}

/** «El más antiguo espera desde hace 3 días (12 oct 2026)» / «…desde hoy (09:15)». */
function oldestWaitingText(days: number | null, iso: string | null) {
  if (days === null || !iso) return null;
  if (days === 0) return `El más antiguo espera desde hoy (${fmt.time(iso)})`;
  return `El más antiguo espera desde hace ${days} día${days === 1 ? '' : 's'} (${fmt.date(iso)})`;
}

function PoolTab({ companyId, projectId }: { companyId: string; projectId?: string }) {
  const { data, isLoading, refetch } = usePool(companyId, projectId);
  const { data: advisors } = useAdvisors(companyId);
  const [distributeOpen, setDistributeOpen] = useState(false);
  // Lead de la lista corta que se está asignando (reutiliza el diálogo de la tabla y la ficha).
  const [assigning, setAssigning] = useState<string | null>(null);
  const leadsLink = (advisorId: string | 'none', extra: Record<string, string> = {}) => {
    const p = new URLSearchParams({
      companyId,
      ...(projectId ? { projectId } : {}),
      ...(advisorId === 'none' ? { unassigned: 'true' } : { advisorId }),
      ...extra,
    });
    return `/pipeline?vista=tabla&${p}`;
  };
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;
  const cols: Array<[keyof PoolAdvisorRow, string, 'default' | 'warning' | 'danger' | 'success']> =
    [
      ['total', 'Total', 'default'],
      ['activos', 'Activos', 'default'],
      ['noGestionados', 'Sin gestionar', 'warning'],
      ['alDia', 'Al día', 'success'],
      ['vencidosHoy', 'Venc. hoy', 'warning'],
      ['vencidosAyer', 'Venc. ayer', 'warning'],
      ['vencidos2a7', 'Venc. 2–7', 'danger'],
      ['vencidosMas7', 'Venc. +7', 'danger'],
      ['tareasVencidas', 'Tareas venc.', 'danger'],
    ];
  const bandParam: Partial<Record<keyof PoolAdvisorRow, Record<string, string>>> = {
    noGestionados: { followupState: 'NO_GESTIONADO' },
    alDia: { followupState: 'AL_DIA' },
    vencidosHoy: { followupState: 'VENCIDO', overdueBand: 'HOY' },
    vencidosAyer: { followupState: 'VENCIDO', overdueBand: 'AYER' },
    vencidos2a7: { followupState: 'VENCIDO', overdueBand: 'DIAS_2_7' },
    vencidosMas7: { followupState: 'VENCIDO', overdueBand: 'MAS_7' },
    activos: { status: 'ACTIVO' },
  };
  return (
    <div className="space-y-4">
      <Card className={data.unassigned.total > 0 ? 'border-amber-300' : ''}>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">
                Sin asignar: {data.unassigned.total} lead{data.unassigned.total === 1 ? '' : 's'}
                {data.unassigned.noGestionados > 0 && (
                  <Badge tone="warning" className="ml-2">
                    {data.unassigned.noGestionados} sin gestionar
                  </Badge>
                )}
              </p>
              {data.unassigned.total > 0 && (
                <p className="text-warning text-sm font-medium" data-testid="pool-oldest">
                  {oldestWaitingText(
                    data.unassigned.oldestWaitingDays,
                    data.unassigned.oldestCapturedAt,
                  )}
                </p>
              )}
              <p className="text-muted-foreground text-sm">
                Repártelos por turnos entre los asesores que elijas o asígnalos uno a uno aquí o
                desde la tabla.
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={leadsLink('none')}>Ver en tabla</Link>
              </Button>
              <Button
                size="sm"
                onClick={() => setDistributeOpen(true)}
                disabled={data.unassigned.total === 0 || !projectId}
                title={!projectId ? 'Elige un proyecto para repartir' : ''}
                data-testid="btn-distribute"
              >
                <Shuffle className="h-4 w-4" /> Repartir
              </Button>
            </div>
          </div>
          {data.unassigned.items.length > 0 && (
            <ul className="divide-y rounded-md border text-sm" data-testid="pool-waiting">
              {data.unassigned.items.map((l) => (
                <li
                  key={l.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  data-testid="pool-waiting-row"
                >
                  <div className="min-w-0">
                    <Link to={`/leads/${l.id}`} className="font-medium hover:underline">
                      {l.contactName}
                    </Link>
                    <p className="text-muted-foreground text-xs">
                      {l.medium ?? 'medio desconocido'} · captado {fmt.dateTime(l.capturedAt)} ·{' '}
                      {l.projectName}
                      {l.followupState === 'NO_GESTIONADO' ? ' · sin gestionar' : ''}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAssigning(l.id)}
                    data-testid="btn-pool-assign"
                  >
                    <UserPlus className="h-4 w-4" /> Asignar
                  </Button>
                </li>
              ))}
              {data.unassigned.total > data.unassigned.items.length && (
                <li className="text-muted-foreground px-3 py-2 text-xs">
                  Se muestran los {data.unassigned.items.length} más antiguos de{' '}
                  {data.unassigned.total};{' '}
                  <Link to={leadsLink('none')} className="underline">
                    ver todos en la tabla
                  </Link>
                  .
                </li>
              )}
            </ul>
          )}
        </CardContent>
      </Card>
      {assigning && (
        <AssignDialog
          leadId={assigning}
          currentAdvisorId={null}
          advisors={advisors ?? []}
          open
          onOpenChange={(o) => {
            if (o) return;
            setAssigning(null);
            void refetch();
          }}
        />
      )}
      <div className="space-y-2 md:hidden" data-testid="pool-cards">
        {data.advisors.map((a) => (
          <div key={a.advisorId} className="bg-card rounded-lg border p-3 text-sm">
            <p className="flex flex-wrap items-center gap-1 font-medium">
              {a.advisorName}
              <PoolBadges row={a} />
            </p>
            <p className="text-muted-foreground text-xs">
              {advisorFunctionLabel(a.advisorFunction)}
            </p>
            <div className="mt-2 grid grid-cols-3 gap-1 text-xs">
              {cols.map(([k, label, tone]) => (
                <Link
                  key={k}
                  to={leadsLink(a.advisorId, bandParam[k])}
                  className="bg-muted/40 rounded px-2 py-1"
                >
                  <span className="text-muted-foreground block">{label}</span>
                  <span
                    className={`font-semibold ${a[k] && tone === 'danger' ? 'text-danger' : a[k] && tone === 'warning' ? 'text-warning' : ''}`}
                  >
                    {a[k]}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-card hidden overflow-x-auto rounded-lg border md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2">Asesor</th>
              {cols.map(([k, label]) => (
                <th key={k} className="px-2 py-2 text-right">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.advisors.map((a) => (
              <tr key={a.advisorId} className="border-t" data-testid="pool-row">
                <td className="px-3 py-2">
                  <p className="flex flex-wrap items-center gap-1 font-medium">
                    {a.advisorName}
                    <PoolBadges row={a} />
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {advisorFunctionLabel(a.advisorFunction)}
                  </p>
                </td>
                {cols.map(([k, , tone]) => (
                  <td key={k} className="px-2 py-2 text-right">
                    {a[k] === 0 ? (
                      <span className="text-muted-foreground">0</span>
                    ) : (
                      <Link
                        to={leadsLink(a.advisorId, bandParam[k])}
                        className={`font-medium hover:underline ${tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : tone === 'success' ? 'text-success' : ''}`}
                      >
                        {a[k]}
                      </Link>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-muted/30 border-t font-semibold">
              <td className="px-3 py-2">Total</td>
              {cols.map(([k]) => (
                <td key={k} className="px-2 py-2 text-right">
                  {data.totals[k as keyof typeof data.totals]}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {projectId && (
        <DistributeDialog
          companyId={companyId}
          projectId={projectId}
          open={distributeOpen}
          onOpenChange={setDistributeOpen}
          total={data.unassigned.total}
        />
      )}
    </div>
  );
}

/** Etiquetas de la fila de la Bolsa: rol (coordinadores y jefes también manejan leads) y «inactivo». */
function PoolBadges({ row }: { row: PoolAdvisorRow }) {
  return (
    <>
      {row.role && row.role !== 'ASESOR' && (
        <Badge tone="outline" data-testid="pool-role">
          {ROLE_LABELS[row.role] ?? row.role}
        </Badge>
      )}
      {!row.active && (
        <Badge
          tone="muted"
          title="Dado de baja o ya sin rol de ventas: sus leads siguen a su nombre"
        >
          inactivo
        </Badge>
      )}
    </>
  );
}

function DistributeDialog({
  companyId,
  projectId,
  open,
  onOpenChange,
  total,
}: {
  companyId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  total: number;
}) {
  // Solo quienes pueden recibir leads de este proyecto (membresía sin proyecto o con este), sin cuentas de prueba.
  const { data: advisors } = useAdvisors(companyId, { projectId });
  const mutation = useDistribute();
  const toast = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  // Lote 6 · I4 (P18): el servidor toma los 1 000 más antiguos por llamada; el resto se reparte en otra.
  const taking = Math.min(total, DISTRIBUTE_MAX_LEADS);
  const capped = total > DISTRIBUTE_MAX_LEADS;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await mutation.mutateAsync({ companyId, projectId, advisorIds: selected });
      // «Se repartieron X; Y no se pudieron: …» y cuántos quedan en la bolsa.
      toast.push(distributeSummary(r, (id) => advisors?.find((a) => a.id === id)?.fullName));
      onOpenChange(false);
    } catch (err) {
      toast.error('No se pudo repartir', (err as Error).message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {taking === 1
                ? 'Repartir el lead más antiguo'
                : `Repartir los ${groupThousands(taking)} leads más antiguos`}
            </DialogTitle>
            <DialogDescription>
              Por turnos, en orden de captación, entre los asesores marcados. Cada asesor recibe una
              notificación.
            </DialogDescription>
          </DialogHeader>
          {capped && (
            <p className="text-warning text-sm font-medium" data-testid="distribute-cap">
              Se reparten los {groupThousands(DISTRIBUTE_MAX_LEADS)} más antiguos; vuelve a repartir
              para el resto.
            </p>
          )}
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
            {advisors
              ?.filter((a) => a.kind === 'HUMAN' && !a.isTestAccount && a.assignable !== false)
              .map((a) => (
                <label
                  key={a.id}
                  className="hover:bg-accent flex items-center gap-2 rounded px-2 py-1 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(a.id)}
                    onChange={() => toggle(a.id)}
                    className="h-4 w-4"
                  />{' '}
                  {a.fullName}
                  <span className="text-muted-foreground text-xs">
                    {advisorFunctionLabel(a.advisorFunction)}
                  </span>
                </label>
              ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={selected.length === 0 || mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Repartir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RankingTab({ companyId, projectId }: { companyId: string; projectId?: string }) {
  const [month, setMonth] = useState(currentMonth());
  const { data, isLoading } = useRanking(companyId, month, projectId);
  const medal = ['🥇', '🥈', '🥉'];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Puntaje = ventas×{RANKING_WEIGHTS.VENTAS} + separaciones×{RANKING_WEIGHTS.SEPARACIONES} +
          visitas×{RANKING_WEIGHTS.VISITAS} + citas concretadas×{RANKING_WEIGHTS.CITAS_CONCRETADAS}{' '}
          − vencidos +7 días×{RANKING_WEIGHTS.OVERDUE_PENALTY}
        </p>
        <Select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          aria-label="Mes"
          className="w-40"
        >
          {monthOptions().map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </Select>
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Asesor</th>
                <th className="px-2 py-2 text-right">Puntos</th>
                <th className="px-2 py-2 text-right">Captaciones</th>
                <th className="px-2 py-2 text-right">Únicos</th>
                <th className="px-2 py-2 text-right">Recontacto</th>
                <th
                  className="px-2 py-2 text-right"
                  title="% contacto efectivo: gestiones efectivas ÷ gestiones de contacto (la misma fórmula que el Embudo)"
                >
                  % cont. efectivo
                </th>
                <th className="px-2 py-2 text-right">Citas</th>
                <th className="px-2 py-2 text-right">Concretadas</th>
                <th className="px-2 py-2 text-right">Visitas</th>
                <th className="px-2 py-2 text-right">Separ.</th>
                <th className="px-2 py-2 text-right">Ventas</th>
                <th className="px-2 py-2 text-right">Venc. +7</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.advisorId} className="border-t" data-testid="ranking-row">
                  <td className="px-3 py-2">{medal[r.position - 1] ?? r.position}</td>
                  <td className="px-3 py-2 font-medium">
                    <span className="flex flex-wrap items-center gap-1">
                      {r.advisorName}
                      {!r.active && (
                        <Badge
                          tone="muted"
                          title="Sus métricas del mes cuentan igual, aunque ya no reciba leads"
                          data-testid="ranking-inactive"
                        >
                          {r.role === null ? 'ya no está en la empresa' : 'inactivo'}
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-semibold">{r.points}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.CAPTACIONES}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.LEADS_UNICOS}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.LEADS_RECONTACTO}</td>
                  <td className="px-2 py-2 text-right">
                    {r.contactabilidad === null ? '—' : `${Math.round(r.contactabilidad * 100)}%`}
                  </td>
                  <td className="px-2 py-2 text-right">{r.metrics.CITAS_GENERADAS}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.CITAS_CONCRETADAS}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.VISITAS}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.SEPARACIONES}</td>
                  <td className="px-2 py-2 text-right">{r.metrics.VENTAS}</td>
                  <td className={`px-2 py-2 text-right ${r.overdue ? 'text-danger' : ''}`}>
                    {r.overdue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function RulesTab({ companyId }: { companyId: string }) {
  const { data: rules } = useAssignmentRules(companyId);
  const { data: projects } = useProjects();
  // Con los dados de baja, para que el resumen de cada regla muestre su nombre y no «asesor».
  const { data: advisors } = useAdvisors(companyId, { includeInactive: true });
  const { data: botReadiness } = useBotReadiness(companyId);
  const upsert = useUpsertRule();
  const toast = useToast();
  const [editing, setEditing] = useState<{ projectId: string; rule?: AssignmentRuleDto } | null>(
    null,
  );
  const mine = (projects ?? []).filter((p) => p.companyId === companyId);
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Reglas de distribución por proyecto</CardTitle>
          <CardDescription>
            Para cada proyecto: quién recibe los leads nuevos que llegan sin asesor (por turnos o
            nadie) y si un lead se reasigna cuando el asesor no lo gestiona en el plazo (horas
            hábiles desde que le llega).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {mine.map((p) => {
            const rule = rules?.find((r) => r.projectId === p.id);
            return (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
                data-testid="rule-row"
              >
                <div>
                  <p className="font-medium">{p.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {rule ? (
                      <>
                        {rule.strategy === 'ROUND_ROBIN'
                          ? `Por turnos entre ${rule.advisorIds.length} asesores${
                              rule.weights
                                ? ` (de cada 10: ${rule.advisorIds
                                    .map(
                                      (id) =>
                                        `${advisors?.find((a) => a.id === id)?.fullName ?? 'asesor'} ${rule.weights?.[id] ?? 0}`,
                                    )
                                    .join(' · ')})`
                                : ''
                            }`
                          : 'Sin reparto automático: quedan sin asignar'}
                        {rule.strategy === 'ROUND_ROBIN' &&
                          rule.active &&
                          rule.advisorIds.length === 0 && (
                            <span
                              className="text-warning block font-medium"
                              data-testid="rule-empty"
                            >
                              Sin asesores: los leads nuevos van a la bolsa hasta que elijas
                              asesores (el bot y la reasignación automática siguen igual).
                            </span>
                          )}
                        {rule.botFirst ? ' · el bot escribe primero' : ''}
                        {rule.autoReassignAfterHours
                          ? ` · se reasigna tras ${rule.autoReassignAfterHours} h hábiles sin gestión desde que llega al asesor (va ${rule.reassignTo === 'POOL' ? 'a la bolsa' : 'a un coordinador'})`
                          : ' · no se quitan leads'}
                        {!rule.active && ' · pausada'} · actualizada {fmt.ago(rule.updatedAt)}
                        {rule.botFirst && rule.active && botReadiness && !botReadiness.ready && (
                          <span className="text-warning block" data-testid="rule-bot-off">
                            {botReadiness.reason}: los leads nuevos de este proyecto reciben la
                            tarea «Primer contacto» y siguen con su asesor.
                          </span>
                        )}
                      </>
                    ) : (
                      'Sin regla: los leads nuevos quedan sin asignar y el coordinador los reparte'
                    )}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing({ projectId: p.id, rule })}
                >
                  {rule ? 'Editar' : 'Configurar'}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>
      {editing && (
        <RuleDialog
          companyId={companyId}
          projectName={mine.find((p) => p.id === editing.projectId)?.name ?? ''}
          projectId={editing.projectId}
          rule={editing.rule}
          botReadiness={botReadiness ?? null}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            try {
              await upsert.mutateAsync(payload);
              toast.success('Regla guardada');
              setEditing(null);
            } catch (err) {
              toast.error('No se pudo guardar', (err as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}

function RuleDialog({
  companyId,
  projectId,
  projectName,
  rule,
  botReadiness,
  onClose,
  onSave,
}: {
  companyId: string;
  projectId: string;
  projectName: string;
  rule?: AssignmentRuleDto;
  /** Estado del bot de la empresa (null mientras carga): apagado o incompleto, la casilla no se puede marcar. */
  botReadiness: BotReadiness | null;
  onClose: () => void;
  onSave: (p: {
    projectId: string;
    strategy: 'MANUAL' | 'ROUND_ROBIN';
    advisorIds: string[];
    weights: Record<string, number> | null;
    autoReassignAfterHours: number | null;
    reassignTo: 'POOL' | 'COORDINATOR';
    coordinatorId: string | null;
    botFirst: boolean;
    active: boolean;
  }) => Promise<void>;
}) {
  // Con los dados de baja y el motivo por proyecto (Lote 6 · I2, P6): los miembros de la regla que ya no pueden
  // recibir leads se muestran con su etiqueta para poder desmarcarlos. Mientras la lista carga (o si falla) los
  // ids guardados se conservan tal cual: nunca se envía una lista vacía por no tener con qué compararlos.
  const {
    data: advisorOptions,
    isLoading: advisorsLoading,
    isError: advisorsFailed,
  } = useAdvisors(companyId, { includeInactive: true, projectId });
  const [strategy, setStrategy] = useState<'MANUAL' | 'ROUND_ROBIN'>(
    rule?.strategy ?? 'ROUND_ROBIN',
  );
  const [advisorIds, setAdvisorIds] = useState<string[]>(rule?.advisorIds ?? []);
  // Reparto parejo (por defecto) o "de cada 10 leads": partes por asesor que deben sumar 10.
  const [even, setEven] = useState(!rule?.weights);
  const [weights, setWeights] = useState<Record<string, number>>(rule?.weights ?? {});
  const [hours, setHours] = useState(
    rule?.autoReassignAfterHours ? String(rule.autoReassignAfterHours) : '',
  );
  const [reassignTo, setReassignTo] = useState<'POOL' | 'COORDINATOR'>(rule?.reassignTo ?? 'POOL');
  const [coordinatorId, setCoordinatorId] = useState(rule?.coordinatorId ?? '');
  const [botFirst, setBotFirst] = useState(rule?.botFirst ?? false);
  const [active, setActive] = useState(rule?.active ?? true);
  // Con el bot apagado, sin número o sin plantilla la casilla no se puede marcar (la guardada se puede desmarcar).
  const botReady = !!botReadiness?.ready;
  const botFirstLocked = !!botReadiness && !botReadiness.ready && !botFirst;
  useEffect(() => {
    setAdvisorIds(rule?.advisorIds ?? []);
    setEven(!rule?.weights);
    setWeights(rule?.weights ?? {});
  }, [rule]);
  const toggle = (id: string) =>
    setAdvisorIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const setEvenMode = (on: boolean) => {
    setEven(on);
    if (!on) setWeights(suggestWeights(advisorIds.filter((id) => rowOf(id)?.assignable)));
  };
  const setWeight = (id: string, value: string) =>
    setWeights((w) => ({ ...w, [id]: Math.max(0, Math.min(WEIGHT_TOTAL, Number(value) || 0)) }));
  // Filas del diálogo (lib/ruleAdvisors.ts): mientras la lista no llega, los ids guardados siguen valiendo; con
  // ella, los no asignables muestran su motivo y un id ausente nunca sale como UUID.
  const { rows: advisors } = ruleAdvisorRows(advisorOptions, advisorIds);
  const rowOf = (id: string) => advisors.find((a) => a.id === id);
  const isValid = (id: string) => rowOf(id)?.assignable ?? false;
  // Guardar, las partes y el resumen cuentan solo a los asesores que hoy pueden recibir leads.
  const validIds = advisorIds.filter(isValid);
  const weightOf = (id: string) => weights[id] ?? 0;
  const weightSum = validIds.reduce((sum, id) => sum + weightOf(id), 0);
  const useWeights = strategy === 'ROUND_ROBIN' && !even && validIds.length > 1;
  const weightsOk = !useWeights || (weightSum === WEIGHT_TOTAL && validIds.every(weightOf));

  // Al turno solo entran asesores de venta reales con acceso al proyecto; los ya guardados en la regla se
  // conservan visibles (con su motivo si ya no pueden recibir leads) para poder desmarcarlos.
  const sellers = advisors.filter(
    (a) => (a.role === 'ASESOR' && !a.isTestAccount && a.assignable) || advisorIds.includes(a.id),
  );
  const coordinators = advisors.filter(
    (a) => (a.role === 'COORDINADOR' || a.role === 'JEFE_COMERCIAL') && a.assignable,
  );
  const coordinatorOptions = coordinators.length
    ? coordinators
    : advisors.filter((a) => a.assignable);
  const nameOf = (id: string) => rowOf(id)?.fullName;
  const chosen = validIds.map(nameOf).filter((n): n is string => !!n);
  const list = (names: string[]) =>
    names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} y ${names.at(-1)}`;
  const hoursNumber = hours ? Number(hours) : null;
  // Pausar la regla siempre se puede; activarla por turnos exige al menos un asesor válido.
  const needsAdvisors = strategy === 'ROUND_ROBIN' && active && validIds.length === 0;
  const invalidChosen = advisorIds.length - validIds.length;
  const shares = list(
    validIds.map((id) => (nameOf(id) ? `${weightOf(id)} a ${nameOf(id)}` : '')).filter(Boolean),
  );
  const summary = [
    strategy === 'ROUND_ROBIN'
      ? chosen.length
        ? useWeights
          ? weightsOk
            ? `De cada ${WEIGHT_TOTAL} leads nuevos de ${projectName} que lleguen sin asesor, ${shares.replace(/^(\d+) a/, '$1 irán a')}.`
            : `Las partes deben sumar ${WEIGHT_TOTAL} (ahora suman ${weightSum}).`
          : `Los leads nuevos de ${projectName} que lleguen sin asesor se repartirán por turnos entre ${list(chosen)}.`
        : 'Elige al menos un asesor para el reparto por turnos.'
      : `Los leads nuevos de ${projectName} que lleguen sin asesor quedarán sin asignar, en la Bolsa, para que el coordinador los reparta.`,
    hoursNumber
      ? `Si el asesor no gestiona el lead en ${hoursNumber} ${hoursNumber === 1 ? 'hora hábil' : 'horas hábiles'} desde que le llega, el lead pasará ${
          reassignTo === 'POOL'
            ? 'a la bolsa de sin asignar (y quienes coordinan el proyecto reciben un aviso)'
            : `al coordinador ${nameOf(coordinatorId) ?? '(elige uno)'}`
        }.`
      : 'No se quitarán leads por falta de gestión.',
    botFirst
      ? botReady
        ? `El bot de atención escribirá primero a cada lead nuevo de ${projectName}; el asesor responsable recibe una alerta cuando el lead esté caliente y, si no interviene, el bot sigue.`
        : `${botReadiness?.reason ?? 'El bot de atención no está listo'}: los leads nuevos de ${projectName} recibirán la tarea «Primer contacto» y seguirán con su asesor hasta que el bot pueda escribir.`
      : '',
    active ? '' : 'La regla está pausada: no se aplicará hasta que la actives.',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (needsAdvisors || !weightsOk) return;
            void onSave({
              projectId,
              strategy,
              // Solo los válidos: los dados de baja o sin acceso al proyecto salen de la regla al guardar.
              advisorIds: validIds,
              weights: useWeights
                ? Object.fromEntries(validIds.map((id) => [id, weightOf(id)]))
                : null,
              autoReassignAfterHours: hoursNumber,
              reassignTo,
              coordinatorId: reassignTo === 'COORDINATOR' ? coordinatorId || null : null,
              botFirst,
              active,
            });
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Distribución · {projectName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="rule-strategy">
              ¿Quién recibe los leads nuevos que llegan sin asesor?
            </Label>
            <Select
              id="rule-strategy"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as 'MANUAL' | 'ROUND_ROBIN')}
            >
              <option value="ROUND_ROBIN">
                Se reparten por turnos entre los asesores elegidos
              </option>
              <option value="MANUAL">
                Nadie: quedan sin asignar y el coordinador los reparte desde la Bolsa
              </option>
            </Select>
            <p className="text-muted-foreground text-xs">
              Aplica a los leads que entran solos (Meta, web, Excel sin asesor). Los que ya traen
              asesor no pasan por aquí.
            </p>
          </div>
          {strategy === 'ROUND_ROBIN' && (
            <div className="space-y-1">
              <Label>Asesores que entran al turno (el número es el orden del reparto)</Label>
              {advisorsLoading && (
                <Skeleton className="h-16 w-full" data-testid="rule-advisors-loading" />
              )}
              {advisorsFailed && (
                <p className="text-warning text-xs" data-testid="rule-advisors-failed">
                  No se pudo cargar la lista de asesores: se conservan los guardados en la regla.
                </p>
              )}
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                {sellers.map((a) => (
                  <label
                    key={a.id}
                    className="hover:bg-accent flex items-center gap-2 rounded px-2 py-1 text-sm"
                    data-testid="rule-advisor"
                  >
                    <input
                      type="checkbox"
                      checked={advisorIds.includes(a.id)}
                      onChange={() => toggle(a.id)}
                      className="h-4 w-4"
                    />{' '}
                    {a.fullName}
                    {!a.assignable && (
                      <Badge
                        tone="warning"
                        title="Ya no puede recibir leads: el turno lo salta. Desmárcalo para sacarlo de la regla."
                        data-testid="rule-advisor-inactive"
                      >
                        {a.inactiveReason ?? 'dado de baja'}
                      </Badge>
                    )}
                    {advisorIds.includes(a.id) && a.assignable && (
                      <span className="text-muted-foreground ml-auto text-xs">
                        #{validIds.indexOf(a.id) + 1}
                      </span>
                    )}
                  </label>
                ))}
                {sellers.length === 0 && (
                  <p className="text-muted-foreground px-2 py-1 text-sm">
                    No hay asesores de venta con acceso a este proyecto.
                  </p>
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                Solo asesores de venta con acceso al proyecto (sin coordinadores ni cuentas de
                prueba). El primer lead va al #1, el siguiente al #2… y vuelve a empezar.
              </p>
              {invalidChosen > 0 && (
                <p className="text-warning text-xs" data-testid="rule-invalid-note">
                  {invalidChosen === 1
                    ? 'Un asesor marcado ya no puede recibir leads: el turno lo salta y al guardar queda fuera de la regla.'
                    : `${invalidChosen} asesores marcados ya no pueden recibir leads: el turno los salta y al guardar quedan fuera de la regla.`}
                </p>
              )}
              {validIds.length > 1 && (
                <div className="space-y-2 rounded-md border p-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={even}
                      onChange={(e) => setEvenMode(e.target.checked)}
                      className="h-4 w-4"
                      data-testid="rule-even"
                    />
                    Repartir en partes iguales
                  </label>
                  {!even && (
                    <div className="space-y-1">
                      <p className="text-muted-foreground text-xs">
                        De cada {WEIGHT_TOTAL} leads nuevos, ¿cuántos recibe cada asesor? Las partes
                        deben sumar {WEIGHT_TOTAL}.
                      </p>
                      {validIds.map((id) => (
                        <div key={id} className="flex items-center gap-2 text-sm">
                          <span className="min-w-0 flex-1 truncate">
                            {nameOf(id) ?? 'Usuario ya no disponible'}
                          </span>
                          <Input
                            type="number"
                            min={1}
                            max={WEIGHT_TOTAL}
                            value={weightOf(id) || ''}
                            onChange={(e) => setWeight(id, e.target.value)}
                            aria-label={`Parte de ${nameOf(id) ?? 'asesor'}`}
                            className="h-8 w-20"
                            data-testid="rule-weight"
                          />
                          <span className="text-muted-foreground w-24 text-right text-xs">
                            de {WEIGHT_TOTAL} · {Math.round((weightOf(id) / WEIGHT_TOTAL) * 100)}%
                          </span>
                        </div>
                      ))}
                      <p
                        className={`text-xs ${weightSum === WEIGHT_TOTAL ? 'text-success' : 'text-danger'}`}
                        data-testid="rule-weights-sum"
                      >
                        Suma: {weightSum} de {WEIGHT_TOTAL}
                        {weightSum === WEIGHT_TOTAL ? ' ✓' : ' (ajusta las partes)'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="rule-hours">
                Reasignar si el asesor no lo gestiona en… (horas hábiles desde que el lead le llega)
              </Label>
              <Input
                id="rule-hours"
                type="number"
                min={1}
                max={720}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="Vacío = nunca se reasigna"
              />
              <p className="text-muted-foreground text-xs">
                Cuenta solo el horario comercial de la empresa (Administración → Empresa) y arranca
                cuando el lead llega al asesor, no en la captación. No se tocan los leads que
                atiende el bot ni las cargas históricas que llegaron con asesor.
              </p>
            </div>
            {hours && (
              <div className="space-y-1">
                <Label htmlFor="rule-target">¿A dónde va el lead que se le quita?</Label>
                <Select
                  id="rule-target"
                  value={reassignTo}
                  onChange={(e) => setReassignTo(e.target.value as 'POOL' | 'COORDINATOR')}
                >
                  <option value="POOL">A la bolsa de sin asignar</option>
                  <option value="COORDINATOR">A un coordinador</option>
                </Select>
              </div>
            )}
            {hours && reassignTo === 'COORDINATOR' && (
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="rule-coord">Coordinador que lo recibe</Label>
                <Select
                  id="rule-coord"
                  value={coordinatorId}
                  onChange={(e) => setCoordinatorId(e.target.value)}
                  required
                >
                  <option value="">Selecciona…</option>
                  {coordinatorOptions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.fullName}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={botFirst}
                onChange={(e) => setBotFirst(e.target.checked)}
                disabled={botFirstLocked}
                className="h-4 w-4"
                data-testid="rule-bot-first"
              />{' '}
              El bot de atención escribe primero a los leads nuevos
            </label>
            {botReadiness && !botReadiness.ready ? (
              <p className="text-warning text-xs" data-testid="rule-bot-first-reason">
                {botReadiness.reason}. Hasta entonces el bot no escribe: los leads nuevos reciben la
                tarea «Primer contacto» y siguen con su asesor.
                {!botReadiness.active && ' Enciende el bot en Administración → Bot de atención.'}
              </p>
            ) : (
              <p className="text-muted-foreground text-xs">
                El asesor sigue siendo el responsable: recibe la alerta cuando el bot marca el lead
                como caliente y puede intervenir cuando quiera. Si no registra nada en el plazo
                (Administración → Empresa → Bot de atención), el bot continúa.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4"
              />{' '}
              Aplicar esta regla
            </label>
            <p className="text-muted-foreground text-xs">
              Desmarca para pausarla sin borrar la configuración: los leads nuevos quedarán sin
              asignar y no se reasignará ningún lead.
            </p>
          </div>
          <p
            className={`rounded-md border px-3 py-2 text-sm ${needsAdvisors ? 'border-warning/50 bg-warning/10' : 'bg-muted/40'}`}
            data-testid="rule-summary"
          >
            {summary}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={needsAdvisors || !weightsOk || advisorsLoading}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
